/**
 * DEFUSE S10 — the HATCH boundary-edge arm (line / arc / ellipticArc edges).
 *
 * Split out of `./blockTransform.ts` (which re-exports the public surface and
 * carries the family's module documentation, including the pattern-direction /
 * anisotropic-spacing notes) purely for file size — the edge math is
 * unchanged. Consumed by `./transformBlockChild.ts`'s Hatch arm; depends on
 * `./blockInsertTransform.ts` and `./blockArcEllipseMapping.ts`.
 */

import type { HatchBoundaryEdge } from "@dxf-ai-editor/drawing-model";

import type { Vec3 } from "../../types";

import {
  normalizeDeg,
  transformBlockChildPoint,
  type BlockInsertTransform,
} from "./blockInsertTransform";
import {
  mapUniformArcAngleDeg,
  transformEllipseAxes,
} from "./blockArcEllipseMapping";

/** Transform one hatch boundary edge (line / arc / ellipticArc). */
export function transformHatchEdge(
  edge: HatchBoundaryEdge,
  basePoint: Vec3,
  t: BlockInsertTransform,
  radiusScale: number,
  mirrored: boolean,
): HatchBoundaryEdge {
  const pt2 = (p: { x: number; y: number }): { x: number; y: number } => {
    const v = transformBlockChildPoint(p, basePoint, t);
    return { x: v.x, y: v.y };
  };
  switch (edge.edgeType) {
    case "line":
      return { ...edge, start: pt2(edge.start), end: pt2(edge.end) };
    case "arc":
      // Endpoint angles map through the exact closed-form uniform mapping
      // (endpoint-faithful: mapped start ↔ mapped original start point); a
      // mirror flips the sweep direction. A full-circle edge (start == end
      // mod 360) stays angle-equal → the downstream `normalizeSweep`
      // full-circle guard keeps firing. Non-uniform arc → elliptic
      // promotion is the S10-XFORM delta.
      return {
        ...edge,
        center: pt2(edge.center),
        radius: edge.radius * radiusScale,
        startAngle: mapUniformArcAngleDeg(edge.startAngle, t),
        endAngle: mapUniformArcAngleDeg(edge.endAngle, t),
        counterclockwise: mirrored
          ? !edge.counterclockwise
          : edge.counterclockwise,
      };
    case "ellipticArc": {
      // @codex #431 P2 — full conjugate-diameter treatment (shared with
      // `transformEllipseGeometry` via `transformEllipseAxes`): the renderer
      // derives the minor radius as |majorAxisEnd| · minorRatio, so a
      // non-uniform scale must RECOMPUTE the ratio and re-anchor the angles
      // to the image principal axis, not just map the major vector. Angles
      // are measured RELATIVE to the major axis (degrees): under uniform
      // scale the anchor is 0 (pure rotation leaves them untouched, a mirror
      // negates them + flips the sweep flag) — the fast path inside
      // `transformEllipseAxes` keeps those cases exact.
      const axes = transformEllipseAxes(
        edge.majorAxisEnd,
        edge.minorRatio,
        t,
      );
      const tMajorDeg = (axes.tMajor * 180) / Math.PI;
      const mapAngle = (deg: number): number =>
        axes.flip
          ? normalizeDeg(-(deg - tMajorDeg))
          : axes.tMajor === 0
            ? deg
            : normalizeDeg(deg - tMajorDeg);
      return {
        ...edge,
        center: pt2(edge.center),
        majorAxisEnd: axes.major,
        minorRatio: axes.ratio,
        startAngle: mapAngle(edge.startAngle),
        endAngle: mapAngle(edge.endAngle),
        counterclockwise: axes.flip
          ? !edge.counterclockwise
          : edge.counterclockwise,
      };
    }
    default:
      return edge;
  }
}
